import React, { useState, useEffect } from 'react';
import { relationshipService } from '../../services/relationshipService';
import type { RelationshipStatistics as StatsType } from '../../types/relationship';

interface Props {
  guestId: string;
}

const RelationshipStatistics: React.FC<Props> = ({ guestId }) => {
  const [stats, setStats] = useState<StatsType | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadStatistics();
  }, [guestId]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const data = await relationshipService.getStatistics(guestId);
      setStats(data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-4">Statistics</h3>

      {/* Connection Counts */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Total Connections</span>
          <span className="text-lg font-bold text-gray-900">{stats.total_connections}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Strong</span>
          <span className="text-sm font-semibold text-red-600">{stats.strong_connections}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Medium</span>
          <span className="text-sm font-semibold text-amber-600">{stats.medium_connections}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Weak</span>
          <span className="text-sm font-semibold text-slate-600">{stats.weak_connections}</span>
        </div>
      </div>

      {/* Shared Stays */}
      <div className="border-t pt-4 mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">Total Shared Stays</span>
          <span className="text-lg font-bold text-blue-600">{stats.total_shared_stays}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Unique Hotels</span>
          <span className="text-sm font-semibold text-gray-900">{stats.unique_hotels}</span>
        </div>
      </div>

      {/* Frequent Companions */}
      {stats.frequent_companions.length > 0 && (
        <div className="border-t pt-4 mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Frequent Companions</h4>
          <div className="space-y-2">
            {stats.frequent_companions.slice(0, 5).map((companion) => (
              <div key={companion.guest_id} className="flex justify-between items-center text-sm">
                <span className="text-gray-700 truncate">{companion.full_name}</span>
                <span className="text-gray-500 ml-2">{companion.shared_stays}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suspicious Patterns Alert */}
      {stats.suspicious_patterns > 0 && (
        <div className="border-t pt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="text-sm font-semibold text-red-800">
                  {stats.suspicious_patterns} Suspicious Pattern{stats.suspicious_patterns > 1 ? 's' : ''}
                </div>
                <div className="text-xs text-red-600">Requires investigation</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {stats.first_encounter && (
        <div className="border-t pt-4 mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Timeline</h4>
          <div className="text-xs text-gray-600 space-y-1">
            <div>First: {new Date(stats.first_encounter).toLocaleDateString()}</div>
            <div>Last: {new Date(stats.last_encounter).toLocaleDateString()}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipStatistics;
